import { useState } from "react";
import { PlusIcon, MailIcon, PencilIcon, TargetIcon, UsersIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SharedGoalCard } from "../../components/SharedGoalCard";
import { ActivityFeed } from "./components/ActivityFeed";
import { InviteDialog } from "./components/InviteDialog";
import { InviteRow } from "./components/InviteRow";
import { MemberRow } from "./components/MemberRow";
import { useCircleData } from "./hooks/useCircleData";
import { useCircleMutations } from "./hooks/useCircleMutations";
import type { CircleTab } from "./types";

export function CirclePage() {
  const [selectedCircleId, setSelectedCircleId] = useState<string | null>(null);
  const [tab, setTab] = useState<CircleTab>("goals");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [circleName, setCircleName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [inviteStatusMessage, setInviteStatusMessage] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const {
    activeCircle,
    activeCircleId,
    circles,
    circlesQuery,
    comments,
    events,
    invites,
    isOwner,
    me,
    members,
    progressQuery,
    sharedGoals,
  } = useCircleData(selectedCircleId);

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setCircleName("");
    setFormError(null);
  };
  const closeInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail("");
    setFormError(null);
  };
  const closeRenameDialog = () => {
    setRenameDialogOpen(false);
    setFormError(null);
  };

  const { createMutation, inviteMutation, leaveMutation, renameMutation } = useCircleMutations({
    activeCircleId,
    onCloseCreate: closeCreateDialog,
    onCloseInvite: closeInviteDialog,
    onCloseRename: closeRenameDialog,
    onError: setFormError,
    onInviteStatus: setInviteStatusMessage,
    onLeaveError: setLeaveError,
    onSelectedCircleId: setSelectedCircleId,
  });

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Accountability</p>
          <h1 className="text-2xl font-semibold tracking-tight">Circle</h1>
        </div>
        <Button onClick={() => { setFormError(null); setCreateDialogOpen(true); }} size="sm">
          <PlusIcon className="w-4 h-4" />
          New circle
        </Button>
      </header>

      {circlesQuery.isPending && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!circlesQuery.isPending && (!circles || circles.length === 0) && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <UsersIcon className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm font-medium mb-1">No circles yet</p>
          <p className="text-xs text-muted-foreground">
            Create a circle to invite friends and stay accountable together.
          </p>
        </div>
      )}

      {circles && circles.length > 0 && (
        <>
          {circles.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {circles.map((circle) => (
                <button
                  key={circle.id}
                  onClick={() => setSelectedCircleId(circle.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    circle.id === activeCircleId
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {circle.name}
                </button>
              ))}
            </div>
          )}

          {activeCircle && activeCircleId && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h2 className="font-semibold text-lg">{activeCircle.name}</h2>
                    {isOwner && (
                      <button
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Rename circle"
                        onClick={() => {
                          setFormError(null);
                          setRenameValue(activeCircle.name);
                          setRenameDialogOpen(true);
                        }}
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {activeCircle.activeMemberCount} member{activeCircle.activeMemberCount !== 1 ? "s" : ""}
                    {isOwner && activeCircle.pendingInviteCount > 0 && (
                      <> - {activeCircle.pendingInviteCount} pending invite{activeCircle.pendingInviteCount !== 1 ? "s" : ""}</>
                    )}
                  </p>
                </div>
                <Badge variant={isOwner ? "default" : "secondary"}>{isOwner ? "Owner" : "Member"}</Badge>
              </div>

              <div className="flex gap-1 border-b border-border">
                {(["goals", "members", "activity"] as CircleTab[]).map((circleTab) => (
                  <button
                    key={circleTab}
                    onClick={() => setTab(circleTab)}
                    className={`px-3 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                      tab === circleTab
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {circleTab}
                  </button>
                ))}
              </div>

              {tab === "goals" && (
                <div className="space-y-3">
                  {progressQuery.isPending && (
                    <p className="text-sm text-muted-foreground">Loading shared goals...</p>
                  )}
                  {!progressQuery.isPending && sharedGoals.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-8 text-center">
                      <TargetIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium mb-1">No shared goals yet</p>
                      <p className="text-xs text-muted-foreground">
                        When people in this circle share goals with you, today's progress shows up here.
                      </p>
                    </div>
                  ) : (
                    sharedGoals.map((goal) => (
                      <SharedGoalCard key={goal.id} goal={goal} ownerName={goal.ownerDisplayName} />
                    ))
                  )}
                </div>
              )}

              {tab === "members" && (
                <div className="space-y-4">
                  {isOwner && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setFormError(null); setInviteStatusMessage(null); setInviteDialogOpen(true); }}
                    >
                      <MailIcon className="w-4 h-4" />
                      Invite member
                    </Button>
                  )}

                  <Card>
                    <CardHeader className="py-3 px-4 space-y-0">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Members</span>
                    </CardHeader>
                    <CardContent className="p-0">
                      {!members || members.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-muted-foreground">No members yet.</p>
                      ) : (
                        members
                          .filter((member) => member.status === "ACTIVE")
                          .map((member, index) => (
                            <div key={member.id}>
                              {index > 0 && <Separator />}
                              <MemberRow
                                member={member}
                                circleId={activeCircleId}
                                canRemove={isOwner && member.userId !== me?.id}
                              />
                            </div>
                          ))
                      )}
                    </CardContent>
                  </Card>

                  {isOwner && invites && invites.filter((invite) => invite.status === "PENDING").length > 0 && (
                    <Card>
                      <CardHeader className="py-3 px-4 space-y-0">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Pending invites</span>
                      </CardHeader>
                      <CardContent className="p-0">
                        {invites
                          .filter((invite) => invite.status === "PENDING")
                          .map((invite, index) => (
                            <div key={invite.id}>
                              {index > 0 && <Separator />}
                              <InviteRow invite={invite} circleId={activeCircleId} />
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  )}
                  {isOwner && inviteStatusMessage && (
                    <p className="text-sm text-muted-foreground">{inviteStatusMessage}</p>
                  )}
                  <div className="pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        if (window.confirm(`Leave ${activeCircle.name}? You'll need a new invite to rejoin.`)) {
                          leaveMutation.mutate();
                        }
                      }}
                      disabled={leaveMutation.isPending}
                    >
                      {leaveMutation.isPending ? "Leaving..." : "Leave circle"}
                    </Button>
                    {leaveError && <p className="text-sm text-muted-foreground mt-1">{leaveError}</p>}
                  </div>
                </div>
              )}

              {tab === "activity" && (
                <ActivityFeed circleId={activeCircleId} events={events} comments={comments} />
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create circle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="circle-name">Circle name</Label>
              <Input
                id="circle-name"
                autoFocus
                value={circleName}
                onChange={(event) => setCircleName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && circleName.trim()) createMutation.mutate(circleName.trim());
                }}
                placeholder="e.g. Morning crew"
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeCreateDialog}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate(circleName.trim())}
              disabled={!circleName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename circle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="rename-circle">Circle name</Label>
              <Input
                id="rename-circle"
                autoFocus
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && renameValue.trim()) renameMutation.mutate(renameValue.trim());
                }}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeRenameDialog}>Cancel</Button>
            <Button
              onClick={() => renameMutation.mutate(renameValue.trim())}
              disabled={!renameValue.trim() || renameMutation.isPending}
            >
              {renameMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InviteDialog
        formError={formError}
        inviteEmail={inviteEmail}
        inviteMutation={inviteMutation}
        isOpen={inviteDialogOpen}
        onClose={closeInviteDialog}
        onSetInviteEmail={setInviteEmail}
      />
    </div>
  );
}
